import Navbar from '../components/Navbar'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const TOC = [
  { id: 'short-version',    label: 'The short version' },
  { id: 'who-we-are',       label: 'Who we are' },
  { id: 'what-we-collect',  label: 'What we collect' },
  { id: 'why-we-collect',   label: 'Why we collect it' },
  { id: 'who-we-share',     label: 'Who we share with' },
  { id: 'payment-data',     label: 'Payment & transaction data' },
  { id: 'financial-retention', label: 'Financial data retention' },
  { id: 'gdpr',             label: 'Your GDPR rights' },
  { id: 'ccpa',             label: 'Your CCPA rights' },
  { id: 'children',         label: 'Children & COPPA' },
  { id: 'security',         label: 'How we protect data' },
  { id: 'retention',        label: 'How long we keep data' },
  { id: 'cookies',          label: 'Cookies' },
  { id: 'admin-access',     label: 'Administrative Access' },
  { id: 'changes',          label: 'Changes to this policy' },
  { id: 'contact',          label: 'Contact us' },
]

export default function PrivacyPolicyPage() {
  return (
    <div className="legal-page">
      <SEO title="Privacy Policy" description="Verse privacy policy — how we collect, use, and protect your personal data." />
      <Navbar />
      <div className="legal-page__inner">
        <header className="legal-page__header">
          <p className="legal-page__label">Legal</p>
          <h1 className="legal-h1">Privacy Policy</h1>
          <p className="legal-page__meta">Last updated: June 2026 · Effective: March 18, 2026</p>
        </header>

        <nav className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc__heading">On this page</p>
          <ol className="legal-toc__list">
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="legal-toc__link">{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="short-version" className="legal-section">
          <h2 className="legal-h2">The short version</h2>
          <div className="legal-callout">
            <ul className="legal-callout__list">
              <li>We collect your email, username, and date of birth to run your account. We collect usage data to improve the platform.</li>
              <li>We never sell your personal data. Ever.</li>
              <li>You can request a copy of your data, correct it, or delete it at any time by emailing <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a> or visiting your <Link to="/settings?tab=Privacy" className="legal-link">privacy settings</Link>.</li>
            </ul>
          </div>
        </section>

        <section id="who-we-are" className="legal-section">
          <h2 className="legal-h2">Who we are</h2>
          <p>
            Verse is a creative writing platform for fanfiction and original stories. We help readers discover stories they love and give writers a home to share their work with the world.
          </p>
          <p>
            When this policy refers to "Verse", "we", "us", or "our", it means the Verse platform and its operators. For privacy-related questions, you can reach us at <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.
          </p>
        </section>

        <section id="what-we-collect" className="legal-section">
          <h2 className="legal-h2">What information we collect</h2>
          <p>We collect information in a few different ways:</p>

          <h3 className="legal-h3">Information you give us</h3>
          <ul className="legal-list">
            <li><strong>Account information:</strong> When you sign up, we collect your username, email address, and date of birth. Your date of birth is used to verify your age and assign the right account type (teen or adult).</li>
            <li><strong>Content you create:</strong> Stories, chapters, comments, and any other content you publish on Verse.</li>
            <li><strong>Profile information:</strong> Any bio, display name, or profile picture you choose to add.</li>
            <li><strong>Transaction data:</strong> Verse Coin purchases, tips, cashouts, and balance information.</li>
          </ul>

          <h3 className="legal-h3">Information we collect automatically</h3>
          <ul className="legal-list">
            <li><strong>Usage data:</strong> Pages you visit on Verse, features you use, time spent reading, and how you navigate the site.</li>
            <li><strong>Device and connection information:</strong> Browser type, operating system, screen size, and general location (country/region level) from your IP address. We also log your IP address and user-agent when you sign in and when you upload content, as part of keeping the platform safe and meeting our legal obligations.</li>
            <li><strong>Cookies:</strong> Small files stored in your browser. See our <Link to="/cookie-policy" className="legal-link">Cookie Policy</Link> for details.</li>
          </ul>
        </section>

        <section id="why-we-collect" className="legal-section">
          <h2 className="legal-h2">Why we collect it</h2>
          <ul className="legal-list">
            <li><strong>To run the platform:</strong> Your account info is needed to create and maintain your account, let you log in, and show your content.</li>
            <li><strong>To improve Verse:</strong> Usage data helps us understand what's working and what isn't, so we can make the platform better.</li>
            <li><strong>To keep it safe:</strong> We use account and technical information — including the IP address and browser details logged when you sign in and when you upload content — to enforce our content policies, protect against abuse and fraud, investigate violations, and comply with our legal obligations (including child-safety reporting and age restrictions under COPPA).</li>
            <li><strong>To send you emails:</strong> We'll email you to confirm your account, notify you of important account changes, and — if you've opted in — send story update notifications or newsletters. You can manage these preferences in your <Link to="/settings?tab=Privacy" className="legal-link">privacy settings</Link>.</li>
            <li><strong>To process transactions:</strong> To process transactions, maintain coin balances, prevent fraud, and comply with tax reporting obligations.</li>
          </ul>
        </section>

        <section id="who-we-share" className="legal-section">
          <h2 className="legal-h2">Who we share it with</h2>
          <p>We take a minimal approach to sharing your data.</p>
          <ul className="legal-list">
            <li><strong>Supabase:</strong> Our database and authentication provider. Your account data and content are stored on Supabase's servers. Supabase is SOC 2 Type II certified and complies with GDPR. <a href="https://supabase.com/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">View Supabase's privacy policy.</a></li>
            <li><strong>We do not use Google Analytics or any third-party tracking services.</strong> Your browsing behavior on Verse is not shared with advertisers, data brokers, or analytics companies.</li>
            <li><strong>Stripe:</strong> Our payment processor. When you purchase Verse Coins or cash out as an author, Stripe processes the transaction. We share only the minimum information required to complete the transaction. Stripe may collect additional identity and banking information directly through Stripe Connect for author cashouts — this data is stored by Stripe, not by Verse. <a href="https://stripe.com/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">View Stripe's privacy policy.</a></li>
            <li><strong>Law enforcement:</strong> We will share data if required by law, court order, or to protect the safety of users — particularly in cases involving illegal content (such as CSAM, which we are required to report).</li>
          </ul>
          <div className="legal-callout legal-callout--highlight">
            <strong>We never sell your personal data.</strong> Full stop. Your information is not a product.
          </div>
        </section>

        <section id="payment-data" className="legal-section">
          <h2 className="legal-h2">Payment & transaction data</h2>
          <p>
            When you purchase Verse Coins or receive tips, we collect and store: transaction amounts, timestamps, coin balances, and transaction history (tips sent, tips received, purchases, and cashouts).
          </p>
          <p>
            All payment processing is handled by <a href="https://stripe.com/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">Stripe</a>. We never store, access, or process your credit card numbers, bank account details, or other sensitive payment information. Stripe's handling of your payment data is governed by <a href="https://stripe.com/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
          </p>
          <p>
            For author cashouts, Stripe Connect may collect additional identity and banking information directly — this data is stored by Stripe, not by Verse.
          </p>
        </section>

        <section id="financial-retention" className="legal-section">
          <h2 className="legal-h2">Financial data retention</h2>
          <p>
            Transaction records (coin purchases, tips, cashouts) are retained for as long as your account is active and for up to 7 years after account deletion as required for tax reporting and legal compliance.
          </p>
          <p>
            You may request a copy of your transaction history at any time through your <Link to="/settings?tab=Privacy" className="legal-link">account settings</Link>.
          </p>
        </section>

        <section id="gdpr" className="legal-section">
          <h2 className="legal-h2">Your rights under GDPR</h2>
          <p>If you are located in the European Union or European Economic Area, you have the following rights under the General Data Protection Regulation (GDPR):</p>
          <ul className="legal-list">
            <li><strong>Access:</strong> You can request a copy of all personal data we hold about you.</li>
            <li><strong>Rectification:</strong> You can ask us to correct inaccurate or incomplete data.</li>
            <li><strong>Erasure ("Right to be forgotten"):</strong> You can ask us to delete your account and all associated personal data.</li>
            <li><strong>Data portability:</strong> You can request your data in a machine-readable format (JSON) via your <Link to="/settings?tab=Privacy" className="legal-link">privacy settings</Link>.</li>
            <li><strong>Object to processing:</strong> You can object to us processing your data for certain purposes (such as analysing how the platform is used).</li>
            <li><strong>Withdraw consent:</strong> If we're processing your data based on your consent (for example, non-essential cookies), you can withdraw that consent at any time via your <Link to="/settings?tab=Privacy" className="legal-link">cookie preferences</Link>.</li>
          </ul>
          <p>
            To exercise any of these rights, email us at <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>. We'll respond within 30 days. You also have the right to lodge a complaint with your national data protection authority.
          </p>
          <p>
            <strong>Limits on these rights.</strong> These rights are not absolute. In limited cases the law requires or permits us to keep certain data, or to decline a deletion or access request — for example, content we are required to preserve and report under child-safety law (18 U.S.C. § 2258A), or records we must keep for tax or other legal-compliance reasons. Where we rely on such an exception, we will explain why unless the law prohibits us from doing so.
          </p>
        </section>

        <section id="ccpa" className="legal-section">
          <h2 className="legal-h2">Your rights under CCPA</h2>
          <p>If you are a California resident, the California Consumer Privacy Act (CCPA) gives you additional rights:</p>
          <ul className="legal-list">
            <li><strong>Right to know:</strong> You can ask what categories of personal data we collect and how we use it.</li>
            <li><strong>Right to delete:</strong> You can ask us to delete your personal data. This right is subject to legal exceptions — we may retain information we are required by law to keep, including content preserved under child-safety law and records kept for tax or legal-compliance purposes.</li>
            <li><strong>Right to opt out of sale:</strong> We don't sell your personal data, so there's nothing to opt out of. But if that ever changes, we'll update this policy and add a "Do Not Sell My Personal Information" link.</li>
            <li><strong>Right to non-discrimination:</strong> We will not treat you differently — no different prices, features, or service levels — for exercising your privacy rights.</li>
          </ul>
          <p>
            To make a CCPA request, email <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a> with the subject line "CCPA Request".
          </p>
        </section>

        <section id="children" className="legal-section">
          <h2 className="legal-h2">Children and COPPA</h2>
          <p>
            Verse requires all users to be at least 13 years old. This is required by the Children's Online Privacy Protection Act (COPPA), a U.S. federal law that restricts the collection of personal data from children under 13.
          </p>
          <ul className="legal-list">
            <li>We ask for your date of birth during sign-up. If you are under 13, you will not be able to create an account.</li>
            <li>If we discover that we have accidentally collected personal data from a child under 13, we will delete it immediately.</li>
            <li>Users aged 13–17 receive a teen account. Teen accounts have mature content automatically hidden and cannot be unlocked.</li>
            <li>Parents or guardians who believe we have collected data from a child under 13 should contact us immediately at <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.</li>
          </ul>
        </section>

        <section id="security" className="legal-section">
          <h2 className="legal-h2">How we protect your data</h2>
          <p>We take data security seriously and use industry-standard measures to protect your information:</p>
          <ul className="legal-list">
            <li><strong>Encryption in transit:</strong> All data sent between your browser and our servers uses HTTPS/TLS encryption.</li>
            <li><strong>Encryption at rest:</strong> Your data is stored encrypted on Supabase's servers.</li>
            <li><strong>Password security:</strong> Passwords are never stored in plain text. Supabase uses bcrypt hashing.</li>
            <li><strong>Access controls:</strong> Only authorised personnel can access user data, and only when necessary.</li>
            <li><strong>Authentication tokens:</strong> We use short-lived access tokens and refresh tokens to keep your session secure.</li>
          </ul>
          <p>
            No system is 100% secure. If you discover a security vulnerability, please report it responsibly to <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.
          </p>
        </section>

        <section id="retention" className="legal-section">
          <h2 className="legal-h2">How long we keep your data</h2>
          <ul className="legal-list">
            <li><strong>Active accounts:</strong> We keep your account data for as long as your account is active.</li>
            <li><strong>After account deletion:</strong> When you delete your account, your personal data (email, date of birth, profile) is deleted within 30 days. Content you've published (stories, comments) may be anonymised rather than deleted, to preserve the reading experience for others — unless you request complete removal.</li>
            <li><strong>Usage and connection logs:</strong> IP address, user-agent, and usage logs are generally kept for a short period (around 90 days) for security and abuse-prevention purposes, unless we are required to preserve them longer under a legal hold or child-safety law.</li>
            <li><strong>Legal holds:</strong> In rare cases, we may need to retain certain data for longer to comply with legal obligations.</li>
          </ul>
          <p>
            You can delete your account and all associated data at any time from your <Link to="/settings?tab=Privacy" className="legal-link">privacy settings</Link>.
          </p>
        </section>

        <section id="cookies" className="legal-section">
          <h2 className="legal-h2">Cookies</h2>
          <p>
            We use cookies to keep you logged in and to understand how Verse is used. You can manage your cookie preferences at any time.
          </p>
          <p>
            For a full list of the cookies we use and what they do, see our <Link to="/cookie-policy" className="legal-link">Cookie Policy</Link>.
            To change your preferences, visit your <Link to="/settings?tab=Privacy" className="legal-link">privacy settings</Link>.
          </p>
        </section>

        <section id="admin-access" className="legal-section">
          <h2 className="legal-h2">Administrative Access</h2>
          <p>
            Verse platform administrators have access to all user content and activity for moderation and safety purposes, regardless of individual privacy settings. This includes content in private groups, hidden profiles, disabled walls, and online status.
          </p>
          <p>
            Content flagged by our safety systems may be preserved and reported to the National Center for Missing & Exploited Children (NCMEC) as required by 18 U.S.C. § 2258A. We do not sell or share user data with third parties except as required by law.
          </p>
        </section>

        <section id="changes" className="legal-section">
          <h2 className="legal-h2">Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we make significant changes, we will notify you by email (at the address associated with your account) at least 14 days before the changes take effect.
          </p>
          <p>
            Minor updates — like fixing typos or clarifying existing language — may be made without notice. The "Last updated" date at the top of this page will always reflect the most recent version.
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2 className="legal-h2">Contact us</h2>
          <p>
            If you have any questions about this Privacy Policy or how we handle your data, we'd love to hear from you.
          </p>
          <ul className="legal-list">
            <li>Email: <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a></li>
            <li>Privacy settings: <Link to="/settings?tab=Privacy" className="legal-link">/privacy-settings</Link></li>
          </ul>
          <p>
            We aim to respond to all privacy-related inquiries within 5 business days.
          </p>
        </section>
      </div>
    </div>
  )
}
